import React, { useEffect, useState } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import { isAuthHydrated } from '../utils/authHydration'
import { getAuthToken } from '../utils/authStorage'
import { handleSessionExpired } from '../utils/sessionExpired'
import { isStudentWorkspacePath } from '../utils/studentWorkspacePath'
import LoadingSpinner from './LoadingSpinner'
import { WorkspaceShellSkeleton } from './Skeleton'

interface ProtectedRouteProps {
  children: React.ReactNode
  requiredRole?: 'student' | 'admin' | 'tutor' | 'school' | 'affiliate'
  fallbackPath?: string
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredRole, 
  fallbackPath 
}) => {
  const { isLoggedIn, user, _hasHydrated } = useAuthStore()
  const location = useLocation()
  const [hydrationReady, setHydrationReady] = useState(isAuthHydrated)

  useEffect(() => {
    if (isAuthHydrated()) {
      setHydrationReady(true)
      return
    }
    const unsub = useAuthStore.persist.onFinishHydration(() => {
      useAuthStore.getState().setHasHydrated(true)
      setHydrationReady(true)
    })
    const timeout = window.setTimeout(() => {
      useAuthStore.getState().setHasHydrated(true)
      setHydrationReady(true)
    }, 1200)
    return () => {
      unsub()
      window.clearTimeout(timeout)
    }
  }, [_hasHydrated])

  const useStudentSkeleton =
    requiredRole === 'student' || isStudentWorkspacePath(location.pathname)

  // Always wait for zustand persist — cold PWA starts can have a token before
  // isLoggedIn rehydrates (or the reverse), which previously looked like logout.
  if (!hydrationReady) {
    return useStudentSkeleton ? <WorkspaceShellSkeleton /> : <LoadingSpinner />
  }

  const hasToken = Boolean(getAuthToken())
  if (isLoggedIn && !hasToken) {
    handleSessionExpired(location.pathname)
    return useStudentSkeleton ? <WorkspaceShellSkeleton /> : <LoadingSpinner />
  }

  // Function to determine the appropriate login page based on route
  const getLoginPath = (pathname: string): string => {
    if (pathname.startsWith('/admin/')) {
      return '/admin/login'
    } else if (pathname.startsWith('/instructor-dashboard') || pathname.startsWith('/tutor/')) {
      return '/tutor'
    } else if (pathname.startsWith('/school-dashboard') || pathname.startsWith('/school/')) {
      return '/school'
    } else if (pathname.startsWith('/affiliate')) {
      return '/affiliate/login'
    } else {
      return '/' // Student workspace auth lives on the homepage
    }
  }

  // If not logged in, redirect to appropriate login page
  if (!isLoggedIn) {
    const loginPath = fallbackPath || getLoginPath(location.pathname)
    console.log('ProtectedRoute: User not logged in, redirecting to:', loginPath)
    return <Navigate to={loginPath} replace state={{ from: location }} />
  }

  // Wrong role: send them to that portal's login (not home), so they can switch accounts.
  if (requiredRole && user?.role !== requiredRole) {
    const loginPath = getLoginPath(location.pathname)
    console.log(
      `ProtectedRoute: User role ${user?.role} doesn't match required role ${requiredRole}, redirecting to:`,
      loginPath,
    )
    return <Navigate to={loginPath} replace state={{ from: location }} />
  }

  // User is authenticated and has required role (if specified)
  console.log('ProtectedRoute: Access granted for user:', user?.role)
  return <>{children}</>
}

export default ProtectedRoute
